import { useEffect, useRef, useState } from "react";
import { Controller, FieldError } from "react-hook-form";
import { translate } from "ui/i18n";

export const INTimeInput = ({
  defaultValue,
  setFormattedTiming,
  label,
  className,
  error,
}: {
  defaultValue: string;
  setFormattedTiming?: (value: string) => void;
  label?: string;
  className?: string;
  error?: FieldError;
}) => {
  const [initialHours, initialMinutes] = defaultValue.split(":");
  const [hours, setHours] = useState<number | undefined>(
    initialHours ? Number(initialHours) : undefined
  );
  const [minutes, setMinutes] = useState<number | undefined>(
    initialMinutes ? Number(initialMinutes) : undefined
  );

  // Sync internal state when defaultValue changes externally (e.g. after setValue from react-hook-form)
  useEffect(() => {
    const [h, m] = defaultValue.split(":");
    setHours(h ? Number(h) : undefined);
    setMinutes(m ? Number(m) : undefined);
  }, [defaultValue]);

  const setFormattedTimingRef = useRef(setFormattedTiming);
  useEffect(() => {
    setFormattedTimingRef.current = setFormattedTiming;
  });

  useEffect(() => {
    if (setFormattedTimingRef.current) {
      if (hours === undefined || minutes === undefined) {
        setFormattedTimingRef.current("");
        return;
      }
      const formattedTiming = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}`;
      setFormattedTimingRef.current(formattedTiming);
    }
  }, [hours, minutes]);

  const completePartialTiming = () => {
    if (hours !== undefined && minutes === undefined) {
      setMinutes(0);
      return;
    }

    if (minutes !== undefined && hours === undefined) {
      setHours(0);
    }
  };

  return (
    <div className={`w-fit ${className}`}>
      {label && (
        <label className="w-full text-sm font-bold leading-6 text-darkGrey-700">
          {label}
        </label>
      )}
      <div className={`flex items-center gap-4 ${label ? "mt-2" : ""}`}>
        <div className="flex flex-col">
          <span className="mb-2 text-sm">
            {translate("ns1:Input.Labels.Hours")}
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={hours !== undefined ? String(hours).padStart(2, "0") : ""}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              if (raw === "") {
                setHours(undefined);
                return;
              }
              setHours(Math.min(23, Math.max(0, Number(raw))));
            }}
            className={`w-full rounded-xl border ${
              error ? "border-red-500" : "border-darkGrey-500"
            } py-3 text-center text-base focus:outline-none focus:ring-2 focus:ring-darkGrey-300 md:w-20 md:py-4`}
            placeholder="00"
            onClick={(e) => e.currentTarget.select()}
            onBlur={completePartialTiming}
          />
        </div>
        <span className="mt-6 text-lg font-bold">:</span>
        <div className="flex flex-col">
          <span className="mb-2 text-sm">
            {translate("ns1:Input.Labels.Minutes")}
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={
              minutes !== undefined ? String(minutes).padStart(2, "0") : ""
            }
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              if (raw === "") {
                setMinutes(undefined);
                return;
              }
              setMinutes(Math.min(59, Math.max(0, Number(raw))));
            }}
            className={`w-full rounded-xl border ${
              error ? "border-red-500" : "border-darkGrey-500"
            } py-3 text-center text-base focus:outline-none focus:ring-2 focus:ring-darkGrey-300 md:w-20 md:py-4`}
            placeholder="00"
            onClick={(e) => e.currentTarget.select()}
            onBlur={completePartialTiming}
          />
        </div>
      </div>
    </div>
  );
};

export const INControlledTimeInput = ({
  control,
  inputName,
  label,
  className,
  required,
}: {
  control: any;
  inputName: string;
  label?: string;
  className?: string;
  required?: boolean;
}) => {
  return (
    <Controller
      name={inputName}
      control={control}
      rules={{
        required: required ? translate("ns1:Input.Errors.Required") : undefined,
      }}
      render={({ field, fieldState }) => (
        <INTimeInput
          defaultValue={field.value ?? ""}
          setFormattedTiming={field.onChange}
          label={label}
          className={className}
          error={fieldState.error}
        />
      )}
    />
  );
};
